import React, { useEffect, useState } from 'react';
import { SectionCard } from '../components/SectionCard';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { useSession } from '../contexts/SessionContext';
import { saveReleaseStorageConfig, type ReleaseStorageConfigRow } from '../api';

type RequestError = {
  response?: { data?: { message?: string } };
  message?: string;
};

function errorMessage(error: unknown) {
  const failure = error as RequestError;
  return (
    failure.response?.data?.message ||
    failure.message ||
    'Failed to save storage config.'
  );
}

function StoragePolicyPage() {
  const { session } = useSession();
  const { workspace, setWorkspaceVersion } = useWorkspace();
  const { storageConfig } = workspace;
  const defaultStorageDraft: ReleaseStorageConfigRow = {
    name: 'Default bundle storage',
    storageMode: 'managed',
    storageProvider: 's3',
    storageBucket: '',
    storageRegion: 'auto',
    storageEndpoint: '',
    storageCdnHost: '',
    storagePrefix: 'cindro/releases',
    storageAccessKeyId: '',
    storageSecretAccessKey: '',
    hasSecretAccessKey: false,
    isActive: true,
    source: 'environment'
  };
  const [storageDraft, setStorageDraft] =
    useState<ReleaseStorageConfigRow | null>(null);
  const [storageBusy, setStorageBusy] = useState(false);
  const [storageError, setStorageError] = useState('');

  useEffect(() => setStorageDraft(storageConfig), [storageConfig]);

  const storageForm = storageDraft ?? defaultStorageDraft;
  const isSelfManaged = storageForm.storageMode === 'self-managed';
  const storageModeLabel =
    storageForm.storageMode === 'dev-local'
      ? 'Local development'
      : storageForm.storageMode === 'self-managed'
        ? 'Workspace storage'
        : 'Cindro managed';

  function patchStorageDraft(patch: Partial<ReleaseStorageConfigRow>) {
    setStorageDraft((current) => ({
      ...(current ?? defaultStorageDraft),
      ...patch
    }));
  }

  async function handleSaveStorageConfig() {
    if (!session || storageBusy) return;
    setStorageBusy(true);
    setStorageError('');
    try {
      const saved = await saveReleaseStorageConfig(session.accessToken, {
        name: storageForm.name,
        storageMode: storageForm.storageMode,
        storageProvider: storageForm.storageProvider,
        storageBucket: storageForm.storageBucket,
        storageRegion: storageForm.storageRegion,
        storageEndpoint: storageForm.storageEndpoint,
        storageCdnHost: storageForm.storageCdnHost,
        storagePrefix: storageForm.storagePrefix,
        storageAccessKeyId: storageForm.storageAccessKeyId,
        storageSecretAccessKey: storageForm.storageSecretAccessKey || undefined,
        isActive: storageForm.isActive
      });
      setStorageDraft(saved);
      setWorkspaceVersion((value) => value + 1);
    } catch (error: unknown) {
      setStorageError(errorMessage(error));
    } finally {
      setStorageBusy(false);
    }
  }

  if (session?.user.isSuperAdmin !== true) {
    return (
      <SectionCard
        label="Infrastructure"
        title="Bundle hosting policy"
        description="This setting controls release storage across every Cindro workspace."
      >
        <p>
          Only a Cindro instance administrator can change the shared storage
          policy. Workspace owner and admin roles do not grant access.
        </p>
      </SectionCard>
    );
  }

  return (
    <SectionCard
      label="Infrastructure"
      title="Bundle hosting policy"
      description="Choose where Cindro stores OTA bundles across all workspaces."
    >
      <div className="settingsForm">
        <div className="workspaceAccessSummary storagePolicySummary">
          <span>
            <strong>{storageModeLabel}</strong>Hosting mode
          </span>
          <span>
            <strong>{storageForm.isActive ? 'Active' : 'Disabled'}</strong>
            Policy state
          </span>
          <span>
            <strong>
              {storageForm.source === 'database' ? 'Saved' : 'Not saved'}
            </strong>
            Instance policy
          </span>
        </div>

        <label className="settingsField">
          <span>Policy name</span>
          <input
            value={storageForm.name}
            onChange={(event) =>
              patchStorageDraft({ name: event.target.value })
            }
            placeholder="Default bundle storage"
          />
        </label>

        <div className="settingsSplit">
          <label className="settingsField">
            <span>Hosting mode</span>
            <select
              value={storageForm.storageMode}
              onChange={(event) =>
                patchStorageDraft({
                  storageMode: event.target.value as
                    | 'managed'
                    | 'self-managed'
                    | 'dev-local'
                })
              }
            >
              <option value="managed">Cindro managed</option>
              <option value="self-managed">Use workspace storage</option>
              <option value="dev-local">Local development</option>
            </select>
          </label>
          <label className="settingsField">
            <span>Policy state</span>
            <select
              value={storageForm.isActive ? 'active' : 'disabled'}
              onChange={(event) =>
                patchStorageDraft({ isActive: event.target.value === 'active' })
              }
            >
              <option value="active">Active</option>
              <option value="disabled">Disabled</option>
            </select>
          </label>
        </div>

        {storageForm.storageMode === 'managed' ? (
          <div className="settingsModeNotice settingsModeNotice--managed">
            <strong>Cindro operates this storage.</strong>
            <span>
              Bundle hosting, credentials, and delivery endpoints are managed
              for the workspace. No cloud account configuration is required.
            </span>
          </div>
        ) : null}
        {storageForm.storageMode === 'dev-local' ? (
          <div className="settingsModeNotice">
            <strong>Local development only.</strong>
            <span>
              Bundles are written to local development storage on the server. No
              cloud credentials are used, and this mode is not suitable for a
              shared or production deployment.
            </span>
          </div>
        ) : null}

        {isSelfManaged ? (
          <div className="storagePolicyFields">
            <div className="settingsSplit">
              <label className="settingsField">
                <span>Provider</span>
                <select
                  value={storageForm.storageProvider}
                  onChange={(event) =>
                    patchStorageDraft({
                      storageProvider: event.target.value as
                        | 's3'
                        | 'r2'
                        | 'custom'
                    })
                  }
                >
                  <option value="s3">Amazon S3</option>
                  <option value="r2">Cloudflare R2</option>
                  <option value="custom">Custom S3-compatible</option>
                </select>
              </label>
              <label className="settingsField">
                <span>Bucket</span>
                <input
                  value={storageForm.storageBucket}
                  onChange={(event) =>
                    patchStorageDraft({ storageBucket: event.target.value })
                  }
                  placeholder="cindro-bundles"
                />
              </label>
            </div>
            <div className="settingsSplit">
              <label className="settingsField">
                <span>Region</span>
                <input
                  value={storageForm.storageRegion}
                  onChange={(event) =>
                    patchStorageDraft({ storageRegion: event.target.value })
                  }
                  placeholder="auto"
                />
              </label>
              <label className="settingsField">
                <span>Endpoint</span>
                <input
                  value={storageForm.storageEndpoint || ''}
                  onChange={(event) =>
                    patchStorageDraft({ storageEndpoint: event.target.value })
                  }
                  placeholder="https://s3.amazonaws.com"
                />
                <span className="fieldHint">
                  Required only for R2 or another S3-compatible provider.
                </span>
              </label>
            </div>
            <div className="settingsSplit">
              <label className="settingsField">
                <span>Public delivery host</span>
                <input
                  value={storageForm.storageCdnHost}
                  onChange={(event) =>
                    patchStorageDraft({ storageCdnHost: event.target.value })
                  }
                  placeholder="https://cdn.example.com"
                />
              </label>
              <label className="settingsField">
                <span>Object prefix</span>
                <input
                  value={storageForm.storagePrefix}
                  onChange={(event) =>
                    patchStorageDraft({ storagePrefix: event.target.value })
                  }
                  placeholder="cindro/releases"
                />
              </label>
            </div>
            <div className="settingsSplit">
              <label className="settingsField">
                <span>Access key ID</span>
                <input
                  value={storageForm.storageAccessKeyId || ''}
                  onChange={(event) =>
                    patchStorageDraft({
                      storageAccessKeyId: event.target.value
                    })
                  }
                  placeholder="AKIA..."
                  autoComplete="off"
                />
              </label>
              <label className="settingsField">
                <span>Secret access key</span>
                <input
                  type="password"
                  value={storageForm.storageSecretAccessKey || ''}
                  onChange={(event) =>
                    patchStorageDraft({
                      storageSecretAccessKey: event.target.value
                    })
                  }
                  placeholder={
                    storageForm.hasSecretAccessKey
                      ? 'Stored — leave blank to keep it'
                      : 'Enter secret access key'
                  }
                  autoComplete="new-password"
                />
              </label>
            </div>
          </div>
        ) : null}

        {storageError && <p className="fieldError">{storageError}</p>}
        <div className="settingsFooter">
          <div className="fieldHint">
            {storageForm.source === 'database'
              ? 'This instance policy is used for release publishing.'
              : 'Save an instance policy to replace the deployment fallback.'}
          </div>
          <button
            type="button"
            className="primaryButton"
            onClick={handleSaveStorageConfig}
            disabled={storageBusy}
          >
            {storageBusy ? 'Saving…' : 'Save storage policy'}
          </button>
        </div>
      </div>
    </SectionCard>
  );
}

export { StoragePolicyPage };
